
import React from 'react';
import { Users, Clapperboard, Eye, MessageCircle, Percent } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string;
  description: React.ReactNode;
  className?: string;
}> = ({ icon, value, description, className }) => {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-border/50 p-5 flex flex-col items-center text-center ${className}`}>
      <div className="mb-3 text-primary/80">
        {icon}
      </div>
      <h3 className="text-3xl md:text-4xl font-bold mb-2">{value}</h3>
      <div className="text-sm text-muted-foreground">{description}</div>
    </div>
  );
};

const StatisticsSection: React.FC = () => {
  const { language } = useLanguage();
  
  const isEnglish = language === 'en';
  
  return (
    <section id="statistics" className="py-12 md:py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
            {isEnglish ? 'Key Statistics' : 'Les Chiffres Clés'}
          </h2>
          
          <div className="relative mb-10">
            <div className="flex justify-center">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-border/50 px-6 py-3 inline-flex items-center gap-2">
                <span className="text-gray-800 dark:text-gray-200">
                  {isEnglish 
                    ? 'Cash prize of 4K€ from 04/25 to 05/06' 
                    : 'Un cash prize de 4K€ du 25/04 au 06/05'}
                </span>
                <span className="bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 text-xs px-2 py-0.5 rounded-full font-medium flex items-center">
                  <span className="size-1.5 bg-green-500 rounded-full mr-1"></span>
                  {isEnglish ? 'Completed' : 'Terminé'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <StatCard 
              icon={<Users className="h-8 w-8" />}
              value="35"
              description={isEnglish ? 'unique creators' : 'créateurs uniques'}
            />
            
            <StatCard 
              icon={<Clapperboard className="h-8 w-8" />}
              value="87"
              description={isEnglish 
                ? 'inspiring & creative content pieces' 
                : 'contenus inspirants & créatifs'}
            />
            
            <StatCard 
              icon={<Eye className="h-8 w-8" />}
              value="5,6"
              description={
                <div className="flex flex-col">
                  <span>{isEnglish ? 'million views' : 'millions de vues'}</span>
                  <span className="text-xs">{isEnglish ? '100% organic' : '100% organique'}</span>
                </div>
              }
            />
            
            <StatCard 
              icon={<MessageCircle className="h-8 w-8" />}
              value="825"
              description={
                <div className="flex flex-col">
                  <span>{isEnglish ? 'thousand interactions' : 'milles interactions'}</span>
                  <span className="text-xs">
                    {isEnglish 
                      ? '(comments, shares, saves & likes)' 
                      : '(commentaires, partages, enregistrements et likes)'}
                  </span>
                </div>
              }
            />
            
            <StatCard 
              icon={<Percent className="h-8 w-8" />}
              value="14,6%"
              description={
                <div className="flex flex-col">
                  <span>
                    {isEnglish 
                      ? 'average engagement rate' 
                      : 'de taux d\'engagement moyen'}
                  </span>
                  <span className="text-xs italic">
                    {isEnglish 
                      ? '(higher than TikTok average: 2 to 6%)' 
                      : '(supérieur à la moyenne sur TikTok: 2 à 6%)'}
                  </span>
                  {/* Removed source link */}
                </div>
              }
              className="md:col-span-2 lg:col-span-1"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatisticsSection;
